import { setSelectors } from './utilities.js';

class RCQuantityInput extends HTMLElement {
  
  constructor() {
    super();
    this.events = {};
    this.selectors = {};
    this._maxAvailable = Infinity;
    this._maxInitialized = false;
    this._continueSelling = false;
    this._dynamicMax = null;
  };

  connectedCallback() { this._initialize(); };

  disconnectedCallback() { this._deinitialize(); };

  _getVariantId(){
    if(this.selectors.variantIdInput && this.selectors.variantIdInput.value){
      const id = Number(this.selectors.variantIdInput.value);
      return Number.isNaN(id) ? null : id;
    };
    return null;
  };

  _getCartItemKey(){
    if(this.selectors.cartItemKeyInput && this.selectors.cartItemKeyInput.value) return this.selectors.cartItemKeyInput.value;
    return null;
  };

  _updateDynamicMax(){
    if(this._continueSelling) { this._dynamicMax = null; return; }
    const vid = this._getVariantId();
    const inCart = (window.umbrellaConfig && typeof window.umbrellaConfig.getInCartQty === 'function')
      ? window.umbrellaConfig.getInCartQty(vid)
      : 0;
    const remaining = Number.isFinite(this._maxAvailable) ? Math.max(0, this._maxAvailable - inCart) : this._maxAvailable;
    this._dynamicMax = remaining;
    if(this.selectors.input){
      if(Number.isFinite(remaining)) this.selectors.input.max = String(remaining);
      else this.selectors.input.removeAttribute('max');
    };
  };

  _onCartState = () => { this._updateDynamicMax(); };

  _onVariantChange = (event) => {
    const variant = event && event.detail && event.detail.data;
    if(!variant || !this.selectors.input) return;

    const initialMax = this.selectors.input.max !== '' ? Number(this.selectors.input.max) : Infinity;
    this._maxAvailable = Number.isFinite(initialMax) ? initialMax : Infinity;

    const inventoryPolicy = variant.inventory_policy || 'deny';
    const managed = variant.inventory_management !== '' && variant.inventory_management != null;
    const qty = typeof variant.inventory_quantity === 'number'
      ? variant.inventory_quantity
      : Number(variant.inventory_quantity || 0);

    // Determine whether we continue selling when out of stock
    this._continueSelling = this.hasAttribute('continue-selling')
      || this.hasAttribute('data-continue-selling')
      || inventoryPolicy === 'continue';

    if(!this._continueSelling && managed && qty <= 0){
      // Out of stock with deny policy: disable input
      this._maxAvailable = 0;
      this.selectors.input.disabled = true;
      this.selectors.input.setAttribute('disabled', 'disabled');
      this.selectors.input.setAttribute('max', '0');
    } else {
      // Variant available or continue policy
      this._maxAvailable = this._continueSelling || !managed ? Infinity : qty;
      this.selectors.input.disabled = false;
      this.selectors.input.removeAttribute('disabled');
    };

    // Recalculate remaining based on new variant and cart state
    this._updateDynamicMax();
    // Reset quantity back to minimum (usually 1) on variant change
    this._resetToMin('variant-change-reset');
  };

  _onCartAdded(event){
    if(!this._input) return;
    const detail = event && event.detail;
    const item = detail && detail.item;
    if(!item) return;
    const currentVariantId = this._getVariantId();
    if(!currentVariantId) return;
    const itemVariantId = typeof item.variant_id === 'number' ? item.variant_id : (typeof item.id === 'number' ? item.id : null);
    if(!itemVariantId || itemVariantId !== currentVariantId) return;
    this._resetToMin('cart-added-reset');
  };

  _resetToMin(reason){
    if(!this._input || this._input.disabled) return;
    const minAttr = this._input.min !== '' ? Number(this._input.min) : 1;
    const min = Number.isNaN(minAttr) ? 1 : minAttr;
    this._input.value = String(min);
    this._input.dispatchEvent(new Event('change', { bubbles: true }));
    this.dispatchEvent(new CustomEvent('quantity-change', {
      bubbles: true,
      composed: true,
      detail: { value: min, source: this, action: reason || 'reset' }
    }));
  };

  _onQuantityIncrease = (e) => {
    if(e && e.preventDefault) e.preventDefault();
    if(!this.selectors.input) return;
    const step = Number(this.selectors.input.step) || 1;
    const max = this.selectors.input.max !== '' ? Number(this.selectors.input.max) : Infinity;
    const baseMax = Number.isFinite(this._maxAvailable) ? Math.min(this._maxAvailable, max) : max;
    const ceiling = (this._dynamicMax != null) ? Math.min(baseMax, this._dynamicMax) : baseMax;
    let value = Number(this.selectors.input.value);
    if(Number.isNaN(value)) value = 0;
    const attempted = value + step;
    if(attempted > ceiling) {
      showToast(`Attempted quantity ${attempted} exceeds available stock of ${ceiling}.`, 'error');
      return;
    };
    const next = attempted;
    this.selectors.input.value = next;
    this.dispatchEvent(new CustomEvent(this.events.quantityIncrement, {
      bubbles: true,
      composed: true,
      detail: { value: next, id: this._getVariantId(), key: this._getCartItemKey() }
    }));
    // Also emit a unified change event so cart/quick-cart listeners
    // can rely on a single "quantityChange" signal.
    this._onInputChange();
  };

  _onQuantityDecrease = (e) => {
    if(e && e.preventDefault) e.preventDefault();
    if(!this.selectors.input) return;
    const step = Number(this.selectors.input.step) || 1;
    const min = this.selectors.input.min !== '' ? Number(this.selectors.input.min) : 0;
    let value = Number(this.selectors.input.value);
    if(Number.isNaN(value)) value = 0;
    const next = Math.max(value - step, min);
    this.selectors.input.value = next;
    this.dispatchEvent(new CustomEvent(this.events.quantityDecrement, {
      bubbles: true,
      composed: true,
      detail: { value: next, id: this._getVariantId(), key: this._getCartItemKey() }
    }));
    // Also emit a unified change event so cart/quick-cart listeners
    // can rely on a single "quantityChange" signal.
    this._onInputChange();
  };

  _onInputChange = (e) => {
    if(!this.selectors.input) return;
    const raw = Number(this.selectors.input.value);
    let value = raw;
    const step = Number(this.selectors.input.step) || 1;
    const min = this.selectors.input.min !== '' ? Number(this.selectors.input.min) : 0;
    const max = this.selectors.input.max !== '' ? Number(this.selectors.input.max) : Infinity;
    const baseMax = Number.isFinite(this._maxAvailable) ? Math.min(this._maxAvailable, max) : max;
    const ceiling = (this._dynamicMax != null) ? Math.min(baseMax, this._dynamicMax) : baseMax;
    if(Number.isNaN(value)) value = min;
    // Snap to step increments relative to min
    value = Math.round((value - min) / step) * step + min;
    value = Math.max(min, Math.min(ceiling, value));
    this.selectors.input.value = value;
    if(!Number.isNaN(raw) && raw > ceiling) {
      showToast(`Attempted quantity ${raw} exceeds available stock of ${ceiling}.`, 'error');
    };
    this.dispatchEvent(new CustomEvent(this.events.quantityChange, {
      bubbles: true,
      composed: true,
      detail: { value, id: this._getVariantId(), key: this._getCartItemKey() }
    }));
  };

  _initialize(){
    this.events = window.umbrellaConfig.events;
    this.selectors = setSelectors(this, {
      input: 'input[type="number"]',
      decrease: '.quantity-decrement',
      increase: '.quantity-increment'
    });
    // form and variantIdInput live outside this component; resolve via closest form
    const formEl = this.closest('form');
    this.selectors.form = formEl || null;
    this.selectors.variantIdInput = formEl ? formEl.querySelector('input[name="id"]') : null;
    this.selectors.cartItemKeyInput = formEl ? formEl.querySelector('input[name="cart_item_key"]') : null;
    // Require all core selectors before wiring up
    if(!this.selectors.input || !this.selectors.decrease || !this.selectors.increase || !this.selectors.form || !this.selectors.variantIdInput) return;
    if(!this._maxInitialized) {
      const initialMax = this.selectors.input.max !== '' ? Number(this.selectors.input.max) : Infinity;
      this._maxAvailable = Number.isFinite(initialMax) ? initialMax : Infinity;
      this._maxInitialized = true;
    };
    this._continueSelling = this.hasAttribute('continue-selling') || this.hasAttribute('data-continue-selling');
    this._updateDynamicMax();
    this.selectors.decrease.addEventListener('click', this._onQuantityDecrease);
    this.selectors.increase.addEventListener('click', this._onQuantityIncrease);
    this.selectors.input.addEventListener('change', this._onInputChange);
    document.addEventListener('cart:state', this._onCartState);
    const ev = (window.umbrellaConfig && window.umbrellaConfig.events && window.umbrellaConfig.events.variantChange) || 'variant:change';
    document.addEventListener(ev, this._onVariantChange);
  };

  _deinitialize(){
    if(this.selectors.increase) this.selectors.increase.removeEventListener('click', this._onQuantityIncrease);
    if(this.selectors.decrease) this.selectors.decrease.removeEventListener('click', this._onQuantityDecrease);
    if(this.selectors.input) this.selectors.input.removeEventListener('change', this._onInputChange);
    document.removeEventListener('cart:state', this._onCartState);
    const ev = (window.umbrellaConfig && window.umbrellaConfig.events && window.umbrellaConfig.events.variantChange) || 'variant:change';
    document.removeEventListener(ev, this._onVariantChange);
  };
};

if(!customElements.get('rc-quantity-input')) customElements.define('rc-quantity-input', RCQuantityInput);